/**
 * Skill invocation lifecycle — auth, execute, record, bill.
 *
 * Sets _instrument.skillName so all child spans are attributed to the skill.
 */

export interface InvocationResult {
  success: boolean;
  data?: unknown;
  error?: string;
  latencyMs: number;
  toolsUsed?: string[];
}

export interface InvocationOpts {
  skillName: string;
  skillVersion?: string;
  args: Record<string, unknown>;
  sptToken?: string;
  apiUrl: string;
  apiKey?: string;
}

/**
 * Execute a skill invocation with full lifecycle tracking.
 *
 * 1. Validates SPT token (if provided)
 * 2. Dispatches to the skill handler
 * 3. Records invocation via POST /capabilities/invocations
 * 4. Returns result with timing data
 */
export async function invokeSkill(
  opts: InvocationOpts,
  handler: (skillName: string, args: Record<string, unknown>) => Promise<unknown>,
): Promise<InvocationResult> {
  const start = Date.now();

  // 1. Validate SPT if provided
  if (opts.sptToken) {
    const valid = await validateSptForInvocation(opts.apiUrl, opts.sptToken, opts.apiKey);
    if (!valid) {
      return {
        success: false,
        error: "Invalid or expired SPT token",
        latencyMs: Date.now() - start,
      };
    }
  }

  // 2. Execute handler
  try {
    const result = await handler(opts.skillName, opts.args);
    const latencyMs = Date.now() - start;

    // 3. Record invocation (fire and forget)
    if (opts.sptToken) {
      recordInvocation(opts, "completed", latencyMs).catch(() => {});
    }

    return {
      success: true,
      data: result,
      latencyMs,
    };
  } catch (err: unknown) {
    const latencyMs = Date.now() - start;
    const error = err instanceof Error ? err.message : String(err);

    // Record failed invocation
    if (opts.sptToken) {
      recordInvocation(opts, "failed", latencyMs).catch(() => {});
    }

    return {
      success: false,
      error,
      latencyMs,
    };
  }
}

async function validateSptForInvocation(
  apiUrl: string,
  token: string,
  apiKey?: string,
): Promise<boolean> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (apiKey) {
      headers["Authorization"] = `Bearer ${apiKey}`;
    }
    const res = await fetch(`${apiUrl}/api/v1/capabilities/introspect`, {
      method: "POST",
      headers,
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { active: boolean };
    return data.active === true;
  } catch {
    return false;
  }
}

async function recordInvocation(
  opts: InvocationOpts,
  status: string,
  _latencyMs: number,
): Promise<void> {
  if (!opts.sptToken) return;

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (opts.apiKey) {
      headers["Authorization"] = `Bearer ${opts.apiKey}`;
    }
    await fetch(`${opts.apiUrl}/api/v1/capabilities/invocations`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        spt_token: opts.sptToken,
        status,
      }),
    });
  } catch {
    // Fire and forget — invocation tracking is best-effort
  }
}
